import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { BAND_ACTIONS, IEventInfoBlockParams } from "Bands/types";
import { EventInfoBlockService } from "Bands/services";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";

export const dispatchGetEventInfoBlocks = (dispatch: Dispatch<any>, event?: string) => {
  dispatch(
    getEventInfoBlocks({
      event,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventInfoBlocks = createAsyncThunk(
  BAND_ACTIONS.getEventInfoBlocks,
  async (
    {
      event,
      itemsPerPage,
      page,
    }: {
      event?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventInfoBlockService.getBlocks({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        page,
        itemsPerPage,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventInfoBlock]: data.eventInfoBlock,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventInfoBlock = (dispatch: Dispatch<any>, blockId: string) => {
  const action = API_ENDPOINTS.EVENT_INFO_BLOCK(blockId);
  return dispatch(getEventInfoBlock({ blockId, action }));
};

export const getEventInfoBlock = createAsyncThunk(
  BAND_ACTIONS.getEventInfoBlock,
  async ({ blockId }: { blockId: string; action: string }, { rejectWithValue }) => {
    try {
      const block = await EventInfoBlockService.getBlock(blockId);

      return block;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventInfoBlock = createAsyncThunk(
  BAND_ACTIONS.createEventInfoBlock,
  async (block: IEventInfoBlockParams, { rejectWithValue }) => {
    try {
      const data = await EventInfoBlockService.createBlock(block);

      const id = Object.values(data?.eventInfoBlock)?.[0]?.attributes?.id;

      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventInfoBlock = createAsyncThunk(
  BAND_ACTIONS.editEventInfoBlock,
  async (props: { id: string; data: IEventInfoBlockParams }, { rejectWithValue }) => {
    try {
      const data = await EventInfoBlockService.editBlock(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventInfoBlock = createAsyncThunk(
  BAND_ACTIONS.deleteEventInfoBlock,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventInfoBlockService.deleteBlock(id);
      return {
        path: API_ENDPOINTS.EVENT_INFO_BLOCK(id),
        dataKey: IDataKeys.eventInfoBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
